import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { matchRoutes, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { GetRolesFn, GetUserDataFn, UserProvider } from '@swo/user-react';
import { UserSettingsProvider } from '@swo/user-settings-react';
import { UserSettingsFn } from '@swo/user-settings-web/src/lib/user-settings-fn';
import { UserSettings } from '@swo/user-settings-web/src/lib/user-settings';
import { UserSettingsProxy } from '@swo/user-settings-web/src/lib/user-settings-proxy';
import { MessageBus } from '@swo/message-bus';
import { AppMessageBusProvider } from '@swo/message-bus-react';
import { UnsubscribeFn } from '@swo/message-bus/src/lib/message-bus';
import { Button } from '@progress/kendo-react-buttons';
import {
  ChatComponent,
  CompanyHeader,
  CypressTestIdKeysEnum,
  HeaderProps,
  key,
  LayoutTypeEnum,
  obfuscateEmail,
  UserInterface,
} from 'swo-ciam-common-react';

import { useLocationChange } from '@src/core/routing/utils/route-utils';
import useRoutes from '@src/core/routing/routes';
import { RoutePathsEnum } from '@src/core/routing/route-paths.enum';
import { DynamicHeaderContext } from '@src/core/dynamic-header-context/dynamic-header-context';
import { useQuickLinks } from '@src/core/routing/components/quick-links-hook';
import { LocaleTranslationKeysEnum } from '@src/core/i18n/locale-translation-keys.enum';
import {
  ApplicationConfigurationKeyEnum,
  ApplicationConfigurationService,
} from '@src/core/application-configuration';
import { useInjectableServices } from '@src/server/service-registry.provider';
import { updateRecentlyVisited } from '@src/domain/common/utils/quick-links/quick-links';
import { translateText } from '@src/domain/common';
import { useFlag } from '@unleash/proxy-client-react';
import useAuth from '@src/core/auth/components/auth0-wrapper';
import Flags from '@src/core/flags/types/flags.enum';
import { AssumeSessionContext } from '@src/core/auth/assume-session/assume-session-provider.component';
import { useProfileLinks } from '../utils/profile-links';

const unsub: UnsubscribeFn = () => {};
const messageBus: MessageBus = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  send: (name: string, value: unknown) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  subscribe: (name: string, callback: (value: unknown) => void) => unsub,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
const getAppUserSettings: UserSettingsFn = (appName: string) =>
  ({
    getSettings: () => Promise.resolve({}),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
    saveSettings: (value: UserSettings) => Promise.resolve(),
  } as UserSettingsProxy);

export const MainLayoutComponent = () => {
  const { user, logout, rawAccessToken } = useAuth();
  const navigate = useNavigate();

  const { LogoutEventService } = useInjectableServices();
  const isAiAssistantEnabled = useFlag(Flags.AiAssistant);

  const mappedToHeaderUser: UserInterface = useMemo(
    () => ({
      email: user?.email,
      // eslint-disable-next-line camelcase
      givenName: user?.firstName,
      // eslint-disable-next-line camelcase
      familyName: user?.lastName,
    }),
    [user]
  );

  const getUserData: GetUserDataFn = useMemo(
    () => Promise.resolve(mappedToHeaderUser),
    [mappedToHeaderUser]
  );
  const getRoles: GetRolesFn = useMemo(() => () => Promise.resolve([]), []);

  const [currentLayoutType, setCurrentLayoutType] = useState(LayoutTypeEnum.Mini);
  const dynamicHeaderContext = useContext(DynamicHeaderContext);
  const routes = useRoutes();

  useLocationChange().then(({ location }) => {
    const [{ route: currentPageHierarchy }] = matchRoutes(routes, location);
    const routePageLayoutType = currentPageHierarchy.layoutType;
    setCurrentLayoutType(routePageLayoutType || LayoutTypeEnum.None);
  });
  const { links: profileLinks, itemInsertionWorkaround } = useProfileLinks();
  const links = useQuickLinks();

  const { logout: sessionLogout, assumedIdentityData } = useContext(AssumeSessionContext);

  const directLogoutHandler = useCallback(async () => {
    await logout();
    await LogoutEventService.createAuditLogoutEvent();
  }, [logout, LogoutEventService]);

  const sessionLogoutHandler = useCallback(async () => {
    await sessionLogout();
    await LogoutEventService.createAuditLogoutEvent();
  }, [sessionLogout, LogoutEventService]);

  const menuItemClicked = useCallback((_url: string, _displayName: string, id: string) => {
    updateRecentlyVisited(id);
  }, []);

  const waffleLinks = useMemo(() => {
    if (
      !links ||
      links.externalLinks.length === 0 ||
      links.internalAppLinks.length === 0 ||
      links.internalRouteLinks.length === 0
    ) {
      return [];
    }

    const allLinks = [
      ...links.externalLinks,
      ...links.internalAppLinks,
      ...links.internalRouteLinks,
    ];

    return allLinks.filter((link) => link.contextType === 'waffle');
  }, [links]);

  const waffleChosen = useRef(null);

  useEffect(() => {
    const handleWaffleRecentVisit = (event: CustomEvent) => {
      const { item } = event.detail;
      updateRecentlyVisited(item.id);
    };

    if (waffleChosen.current) {
      return () => {
        document.removeEventListener('waffle-recent-visit', handleWaffleRecentVisit);
        waffleChosen.current = null;
      };
    }

    document.addEventListener('waffle-recent-visit', handleWaffleRecentVisit);
    waffleChosen.current = true;

    return () => {
      document.removeEventListener('waffle-recent-visit', handleWaffleRecentVisit);
      waffleChosen.current = null;
    };
  }, [waffleChosen]);

  const temporaryHeaderProps: HeaderProps = useMemo(
    () => ({
      auth0Client: undefined,
      messageBus,
      getAppUserSettings,
      getUserData,
      getRoles,
      layoutType: currentLayoutType,
      dynamicHeaderContext,
      shouldShowSettings: {
        [RoutePathsEnum.Reports]: true,
      },
      helpUrl: ApplicationConfigurationService.getValue(
        ApplicationConfigurationKeyEnum.helpUrl
      ) as string,
      user: mappedToHeaderUser,
      logout: directLogoutHandler,
      title: translateText(LocaleTranslationKeysEnum.HeaderQuickLinkMenu),
      productName: translateText(LocaleTranslationKeysEnum.SwoProductNameTitle),
      menuLinks: links,
      waffleTextTranslations: {
        tooltip: translateText(LocaleTranslationKeysEnum.WaffleTooltip),
        menuTitle: translateText(LocaleTranslationKeysEnum.WaffleMenuTitle),
        screenReaderOpenHint: translateText(LocaleTranslationKeysEnum.WaffleScreenReaderOpenHint),
        emptyTitle: translateText(LocaleTranslationKeysEnum.WaffleEmptyTitle),
        emptyDescription: translateText(LocaleTranslationKeysEnum.WaffleEmptyDescription),
        errorTitle: translateText(LocaleTranslationKeysEnum.WaffleErrorTitle),
        errorDescription: translateText(LocaleTranslationKeysEnum.WaffleErrorDescription),
        contactAdminLabel: translateText(LocaleTranslationKeysEnum.WaffleContactAdminLabel),
        retryLabel: translateText(LocaleTranslationKeysEnum.WaffleRetryLabel),
        labels: waffleLinks.reduce(
          (acc, link) => ({
            ...acc,
            [link.url]: link.displayName,
          }),
          {}
        ),
      },
      navigate: (url: string) => navigate(url, { replace: true }),
      workaroundTheInsertionOfCustomItemsInSWOProfile: profileLinks,
      workaroundTheInsertionOfCustomItemsStyleAdjust: itemInsertionWorkaround,
      menuClickHandler: menuItemClicked,
      sessionPanelProps: {
        emulatedText: {
          emulatedUserText: translateText(LocaleTranslationKeysEnum.SessionPanelEmulatedUserText),
          additionalText:
            assumedIdentityData?.assumedEmail &&
            obfuscateEmail(assumedIdentityData.assumedEmail, {
              obfuscationSymbol: ApplicationConfigurationService.getValue(
                ApplicationConfigurationKeyEnum.sensitiveInformationObfuscationSymbol
              ) as string,
              obfuscationLength: ApplicationConfigurationService.getValue(
                ApplicationConfigurationKeyEnum.sensitiveInformationObfuscationLength
              ) as number,
            }),
        },
        remainingTimeText: translateText(LocaleTranslationKeysEnum.SessionPanelRemainingTime),
        defaultButton: {
          text: translateText(LocaleTranslationKeysEnum.End),
          onClick: sessionLogoutHandler,
        },
      },
    }),
    [
      navigate,
      currentLayoutType,
      dynamicHeaderContext,
      links,
      waffleLinks,
      assumedIdentityData.assumedEmail,
      directLogoutHandler,
      sessionLogoutHandler,
      mappedToHeaderUser,
      menuItemClicked,
      itemInsertionWorkaround,
      profileLinks,
      getUserData,
      getRoles,
    ]
  );

  const createWebSocketUrl = useMemo((): string => {
    const url = new URL(
      ApplicationConfigurationService.getValue(
        ApplicationConfigurationKeyEnum.aiAssistantUrl
      ) as string
    );
    url.protocol = 'wss:';
    url.searchParams.append('token', rawAccessToken);
    return url.toString();
  }, [rawAccessToken]);

  const { pathname } = useLocation();

  const LoadSettingView = useCallback(() => {
    const reports = (
      <Button
        style={{
          width: 'auto',
        }}
        data-testid={key(CypressTestIdKeysEnum.BUTTON, CypressTestIdKeysEnum.ACTION_OPEN)}
        type="button"
        className="k-secondary"
        themeColor="secondary"
        fillMode="solid"
        onClick={(event) => {
          event.stopPropagation();
          navigate(RoutePathsEnum.ReportsAvailableReports);
        }}
      >
        {translateText(LocaleTranslationKeysEnum.SubHeaderSettings)}
      </Button>
    );

    return pathname.includes('reports') ? reports : null;
  }, [pathname, navigate]);

  if (temporaryHeaderProps.layoutType === LayoutTypeEnum.None) {
    return <Outlet />;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="swo-common-react-layout">
      <div
        style={{
          position: 'fixed',
          width: '100%',
          background: 'var(--brand-white)',
        }}
        className="layout-header swo-common-react-header"
      >
        <UserProvider getRoles={getRoles} getUserData={getUserData}>
          <UserSettingsProvider appName="cmp" userSettingsFn={getAppUserSettings}>
            <AppMessageBusProvider bus={messageBus}>
              <CompanyHeader {...temporaryHeaderProps} renderSettings={<LoadSettingView />} />
            </AppMessageBusProvider>
          </UserSettingsProvider>
        </UserProvider>
      </div>

      <main className="swo-common-react-main swo-common-react-y-scrollable">
        <div className="swo-common-react-container">
          <Outlet />

          {isAiAssistantEnabled && (
            <ChatComponent
              wsUrl={createWebSocketUrl}
              user={user.email}
              chat={{
                header: {
                  title: translateText(LocaleTranslationKeysEnum.AssistantTitle),
                  subtitle: translateText(LocaleTranslationKeysEnum.AssistantSubtitle),
                  resetLabel: translateText(LocaleTranslationKeysEnum.AssistantClearHistory),
                },
                assistantButton: {
                  tooltipShowingTextText: translateText(
                    LocaleTranslationKeysEnum.AssistantButtonTooltipOpenAssistant
                  ),
                  tooltipHidingText: translateText(
                    LocaleTranslationKeysEnum.AssistantButtonTooltipHideAssistant
                  ),
                },
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
};
